import type { HwCatalogueDevice } from '@/api/hardwareCatalogue';
import type { StockItem } from '@/types/oscar';

/**
 * What the Hardware catalogue says about the stock behind each device.
 *
 * Anthony, 14/10/2026: *"the team is confused about the difference of hardware
 * catalogue and stock - we should link them/consolidate or make it easier to
 * understand."* He chose one device-centric screen: each device shows what is on
 * the shelf for it, per warehouse.
 *
 * ⛔ THE TWO RECORDS STAY TWO, ON PURPOSE. The warehouse thinks in spec tiers -
 * "High Spec", "OLT" - and a requester asks for "Laptop". Neither list is a
 * subset of the other, and merging them would push warehouse detail onto the
 * request form. What was missing was not a merge, it was the LINK being visible
 * from the side people start on: it could be read and edited only on the Stock
 * page, and from here only when a device was first created.
 *
 * ⚠ Everything here is derived from what the two APIs already return - the
 * catalogue carries each device's company total, and each stock line carries the
 * devices it answers - so this adds no route and no schema.
 */

export type StockTone = 'none' | 'ok' | 'warn' | 'bad';

export interface StockReading {
  label: string;
  tone: StockTone;
  /** What the reading means, for a tooltip - the part that stops it being mistaken for a fault. */
  hint: string;
}

const lines = (n: number) => `${n} ${n === 1 ? 'line' : 'lines'}`;

/**
 * One reading of a device's stock, from the numbers the catalogue already holds.
 *
 * ⛔ "NOT STOCKED" IS NOT A FAULT. Most devices are not held: an iPhone comes
 * from the telco and a label printer is bought per request. Drawing zero lines
 * as an error would put a warning on half the catalogue and teach people to
 * ignore it - so it is neutral, and says what it means.
 *
 * ⚠ AND "NONE ON HAND" IS NOT "NOT STOCKED". The first is a stock problem
 * somebody can fix by ordering; the second is a decision that was never a
 * problem. Collapsing them was how "we hold none" and "there is no such thing"
 * looked identical in the approver's availability list too.
 *
 * ⚠ LOW IS THE SERVER'S ANSWER, not re-derived here (080): the company total
 * against the device's own threshold. Recomputing it would make this screen and
 * the Stock page disagree.
 */
export function stockReading(
  d: Pick<HwCatalogueDevice, 'StockLines' | 'TotalOnHand' | 'IsLow' | 'IsAccessoryOnly'>,
): StockReading {
  if (d.IsAccessoryOnly) {
    return {
      label: 'Holds no stock',
      tone: 'none',
      hint: 'This is the “no device needed” option. It is not a thing on a shelf.',
    };
  }

  if (d.StockLines === 0) {
    return {
      label: 'Not stocked',
      tone: 'none',
      hint: 'Nothing on the Stock page answers this device, so a request for it is fulfilled by purchase.',
    };
  }

  if (d.TotalOnHand <= 0) {
    return {
      label: `None on hand · ${lines(d.StockLines)}`,
      tone: 'bad',
      hint: 'Stock lines answer this device, but there is nothing on the shelf.',
    };
  }

  if (d.IsLow) {
    return {
      label: `${d.TotalOnHand} on hand · Low`,
      tone: 'warn',
      hint: 'Below the “hold at least” level, counted across every warehouse.',
    };
  }

  return {
    label: `${d.TotalOnHand} on hand · ${lines(d.StockLines)}`,
    tone: 'ok',
    hint: 'Counted across every warehouse.',
  };
}

/** The stock lines that answer one device, by name. */
export function linesFor(deviceOptionId: number, items: StockItem[]): StockItem[] {
  return items
    .filter((i) => i.Devices.some((d) => d.DeviceOptionId === deviceOptionId))
    .sort((a, b) => a.Name.localeCompare(b.Name));
}

/**
 * Stock lines that could be linked to this device.
 *
 * ⚠ DEVICE-TYPE LINES ONLY. An accessory or a consumable answers no device, and
 * is low on its own `MinQty` - offering one here would link a cable to a laptop.
 *
 * ⚠ LINES THAT ANSWER NOTHING COME FIRST. Those are the ones that can never be
 * given to a request from stock, so they are what somebody opening this list is
 * most likely to be looking for. A line that already answers a DIFFERENT device
 * is still offered, because one line may answer several (several spec tiers can
 * stand behind one thing a requester asks for).
 */
export function linkableLines(deviceOptionId: number, items: StockItem[]): StockItem[] {
  return items
    .filter((i) => i.ItemType === 'Device'
      && !i.Devices.some((d) => d.DeviceOptionId === deviceOptionId))
    .sort((a, b) => {
      const aNone = a.Devices.length === 0 ? 0 : 1;
      const bNone = b.Devices.length === 0 ? 0 : 1;
      return aNone - bNone || a.Name.localeCompare(b.Name);
    });
}

/**
 * Device-type stock lines that answer NO device.
 *
 * ⛔ THE ONES THAT CAN NEVER BE ALLOCATED. A line mapped to nothing satisfies
 * nothing and the fulfilment picker will not offer it (076) - the conservative
 * reading of a half-configured line, and silent unless somebody looks here.
 */
export function orphanLines(items: StockItem[]): StockItem[] {
  return items
    .filter((i) => i.ItemType === 'Device' && i.Devices.length === 0)
    .sort((a, b) => a.Name.localeCompare(b.Name));
}

/**
 * The set of devices a line should answer after linking or unlinking one.
 *
 * ⛔ THE API REPLACES THE WHOLE SET (`PUT /api/stock-items/{id}/devices`), so
 * sending only "this device" would unlink every other device the line answers.
 * The caller passes the line's CURRENT set and gets the complete new one back.
 *
 * ⚠ A SET, NOT A LIST: linking a device the line already answers changes
 * nothing, and the result is sorted so it compares and reads the same however
 * it was reached.
 */
export function nextDeviceSet(
  current: number[],
  deviceOptionId: number,
  action: 'link' | 'unlink',
): number[] {
  const set = new Set(current);
  if (action === 'link') set.add(deviceOptionId);
  else set.delete(deviceOptionId);
  return [...set].sort((a, b) => a - b);
}
